import { useState, useEffect, useRef } from 'react';
import { cloudRomService } from '../services/CloudRomService';
import type { CloudRom, RomUploadProgress } from '../services/CloudRomService';
import toast from 'react-hot-toast';
import '../styles/rom-manager.css';

export default function RomManager() {
  const [rom, setRom] = useState<CloudRom | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadRom();
  }, []);

  const loadRom = async () => {
    try {
      setIsLoading(true);
      const userRom = await cloudRomService.getUserRom();
      setRom(userRom);
    } catch (error) {
      console.error('Failed to load ROM:', error);
      toast.error('Failed to load ROM information');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = async (file: File) => {
    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Validate ROM file
      const validation = await cloudRomService.validateRomFile(file);
      if (!validation.valid) {
        toast.error(validation.error || 'Invalid ROM file');
        setIsUploading(false);
        return;
      }

      // Calculate hash and extract metadata
      toast.loading('Processing ROM file...', { id: 'rom-process' });
      const [romHash, metadata] = await Promise.all([
        cloudRomService.calculateRomHash(file),
        cloudRomService.extractRomMetadata(file),
      ]);
      toast.dismiss('rom-process');

      // Check if replacing existing ROM
      if (rom) {
        const confirm = window.confirm(
          `You already have a ROM uploaded (${rom.romName}).\n\nUploading a new ROM will replace it and delete all associated save files.\n\nDo you want to continue?`
        );
        if (!confirm) {
          setIsUploading(false);
          return;
        }
      }

      // Upload ROM
      toast.loading('Uploading ROM...', { id: 'rom-upload' });
      const response = await cloudRomService.uploadRom(
        file,
        romHash,
        metadata.romName,
        metadata.gameCode,
        metadata.makerCode,
        (progress: RomUploadProgress) => {
          setUploadProgress(progress.percentage);
        }
      );

      toast.dismiss('rom-upload');
      toast.success(
        response.replaced
          ? 'ROM replaced successfully'
          : 'ROM uploaded successfully'
      );

      // Reload ROM info
      await loadRom();
    } catch (error: any) {
      console.error('Failed to upload ROM:', error);
      toast.dismiss('rom-upload');
      const errorMessage =
        error.response?.data?.message || 'Failed to upload ROM';
      toast.error(errorMessage);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDownloadRom = async () => {
    if (!rom) return;

    try {
      toast.loading('Downloading ROM...', { id: 'rom-download' });
      const romData = await cloudRomService.downloadRom();

      // Create download link
      const blob = new Blob([romData as BlobPart], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${rom.romName}.nds`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.dismiss('rom-download');
      toast.success('ROM downloaded successfully');
    } catch (error) {
      console.error('Failed to download ROM:', error);
      toast.dismiss('rom-download');
      toast.error('Failed to download ROM');
    }
  };

  const handleDeleteRom = async () => {
    if (!rom) return;

    const confirm = window.confirm(
      `Are you sure you want to delete "${rom.romName}"?\n\nThis will also delete all associated save files.\n\nThis action cannot be undone.`
    );

    if (!confirm) return;

    try {
      await cloudRomService.deleteRom();
      toast.success('ROM deleted successfully');
      setRom(null);
    } catch (error) {
      console.error('Failed to delete ROM:', error);
      toast.error('Failed to delete ROM');
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    const file = files[0];
    await handleFileSelect(file);
  };

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    await handleFileSelect(files[0]);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (isLoading) {
    return (
      <div className="rom-manager">
        <div className="loading">Loading ROM information...</div>
      </div>
    );
  }

  return (
    <div className="rom-manager">
      <div className="section-header">
        <h2>ROM Storage</h2>
        <span className="storage-limit">
          {rom ? '1/1 ROM used' : '0/1 ROM available'}
        </span>
      </div>

      {rom ? (
        <div className="rom-card">
          <div className="rom-info">
            <h3>{rom.romName}</h3>
            {rom.gameCode && (
              <p className="rom-meta">Game Code: {rom.gameCode}</p>
            )}
            {rom.makerCode && (
              <p className="rom-meta">Maker Code: {rom.makerCode}</p>
            )}
            <p className="rom-meta">
              Size: {cloudRomService.formatFileSize(rom.fileSize)}
            </p>
            <p className="rom-meta">
              Uploaded: {new Date(rom.uploadDate).toLocaleDateString()}
            </p>
            <p className="rom-hash" title={rom.romHash}>
              Hash: {rom.romHash.substring(0, 16)}...
            </p>
          </div>
          <div className="rom-actions">
            <button
              className="btn btn-primary"
              onClick={handleDownloadRom}
              aria-label={`Download ${rom.romName}`}
            >
              Download
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => fileInputRef.current?.click()}
              aria-label="Replace ROM"
            >
              Replace
            </button>
            <button
              className="btn btn-danger"
              onClick={handleDeleteRom}
              aria-label={`Delete ${rom.romName}`}
            >
              Delete
            </button>
          </div>
        </div>
      ) : (
        <div
          className={`rom-upload-zone ${isDragging ? 'dragging' : ''} ${
            isUploading ? 'uploading' : ''
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {isUploading ? (
            <div className="upload-progress">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="progress-text">{uploadProgress}% uploaded</p>
            </div>
          ) : (
            <>
              <div className="upload-icon">📁</div>
              <p className="upload-text">
                Drag and drop a Nintendo DS ROM file here
              </p>
              <p className="upload-subtext">or</p>
              <button
                className="btn btn-primary"
                onClick={() => fileInputRef.current?.click()}
              >
                Choose File
              </button>
              <p className="upload-info">
                Supported formats: .nds, .srl, .dsi (max 512MB)
              </p>
              <p className="legal-notice">
                You must own the game legally. This platform does not provide
                copyrighted content.
              </p>
            </>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".nds,.srl,.dsi"
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />
    </div>
  );
}
